import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle2,
  Gauge,
  ListChecks,
  ShieldAlert,
  Stethoscope,
  TriangleAlert,
} from "lucide-react";
import { fetchFault } from "@/faults-app/lib/db";
import { AppShell, StatusBadge, severityToneKey } from "@/faults-app/components/app-shell";
import { faultIcon } from "@/faults-app/components/electric-icons";

export const Route = createFileRoute("/fault/$id")({
  head: () => ({
    meta: [
      { title: "تفاصيل العطل الكهربائي" },
      {
        name: "description",
        content: "شرح العطل الكهربائي بالعربي: الأعراض، الأسباب، الاختبارات، القراءات والحلول.",
      },
      { property: "og:title", content: "تفاصيل العطل الكهربائي" },
      {
        property: "og:description",
        content: "بطاقة عطل كاملة مع خطوات الفحص والقراءات الطبيعية وتحذيرات السلامة.",
      },
    ],
  }),
  component: FaultPage,
});

function FaultPage() {
  const { id } = Route.useParams();
  const { data: fault, isLoading } = useQuery({
    queryKey: ["fault", id],
    queryFn: () => fetchFault(id),
  });

  if (isLoading)
    return (
      <AppShell>
        <p className="p-10 text-center text-muted-foreground">جارٍ التحميل...</p>
      </AppShell>
    );
  if (!fault)
    return (
      <AppShell>
        <p className="p-10 text-center">لم يتم العثور على العطل المطلوب.</p>
      </AppShell>
    );

  const Ico = faultIcon(`${fault.name_ar} ${fault.device_ar}`);

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-3xl px-4 pt-6">
        <Link
          to="/library"
          search={{ cat: undefined, q: "" }}
          className="inline-flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-primary-dark"
        >
          <ArrowRight className="h-4 w-4" /> مكتبة الأعطال
        </Link>

        <header className="mt-3 rounded-3xl panel-dark p-5 shadow-card">
          <div className="flex items-start gap-3">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
              <Ico className="h-7 w-7" />
            </span>
            <div className="min-w-0">
              <h1 className="text-xl font-extrabold text-white sm:text-2xl">{fault.name_ar}</h1>
              <p className="mt-1 text-sm text-primary">الجهاز: {fault.device_ar}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <StatusBadge tone={severityToneKey(fault.severity)}>
                  <TriangleAlert className="h-3.5 w-3.5" /> درجة الخطورة: {fault.severity}
                </StatusBadge>
                {fault.error_code && (
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white">
                    {fault.error_code}
                  </span>
                )}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/85">{fault.description_ar}</p>
        </header>

        <Section icon={<ListChecks className="h-5 w-5" />} title="الأعراض">
          <ul className="space-y-2">
            {fault.symptoms.map((s, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={<Gauge className="h-5 w-5" />} title="الأسباب المحتملة ودرجة الاحتمال">
          <ul className="space-y-3">
            {fault.causes.map((c, i) => (
              <li key={i}>
                <div className="flex justify-between text-sm font-bold">
                  <span className="min-w-0 pe-2">{c.cause}</span>
                  <span className="shrink-0 text-primary-dark">{c.probability}%</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-linear-to-l from-primary-dark to-primary-glow"
                    style={{ width: `${c.probability}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={<Gauge className="h-5 w-5" />} title="الاختبارات المطلوبة وطريقة القياس">
          <div className="space-y-3">
            {fault.tests.map((t, i) => (
              <div key={i} className="rounded-2xl border border-border bg-muted/40 p-3 text-sm">
                <p className="flex items-center gap-2 font-extrabold">
                  <Gauge className="h-4 w-4 text-primary-dark" /> {t.test}
                </p>
                <p className="mt-2">مكان القياس: {t.location}</p>
                <p>طريقة القياس: {t.method}</p>
                <p className="mt-2 flex gap-2 text-success">
                  <CheckCircle2 className="h-4 w-4 shrink-0" /> القراءة الطبيعية: {t.normal}
                </p>
                <p className="flex gap-2 text-destructive">
                  <TriangleAlert className="h-4 w-4 shrink-0" /> قراءة تدل على عطل: {t.faulty}
                </p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={<CheckCircle2 className="h-5 w-5" />} title="الحلول المقترحة">
          <ul className="space-y-2">
            {fault.solutions.map((s, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </Section>

        {fault.safety_warnings.length > 0 && (
          <Section icon={<ShieldAlert className="h-5 w-5" />} title="تحذيرات السلامة">
            <ul className="space-y-2 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm font-bold text-destructive">
              {fault.safety_warnings.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <ShieldAlert className="h-4 w-4 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {fault.next_question_key && (
          <Link
            to="/diagnose"
            search={{ node: fault.next_question_key }}
            className="press mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-4 font-extrabold text-primary-foreground shadow-glow transition hover:bg-primary-dark hover:text-white sm:w-auto"
          >
            <Stethoscope className="h-5 w-5" /> ابدأ التشخيص التفاعلي لهذا العطل
          </Link>
        )}
      </main>
    </AppShell>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-5 card-tech p-5">
      <h2 className="mb-3 flex items-center gap-2 text-base font-extrabold">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-steel text-primary">
          {icon}
        </span>
        {title}
      </h2>
      {children}
    </section>
  );
}
